/**
 * WelcomeHero — large focal card at the top of the home page.
 *
 * Shows a personalized greeting, the clinic, the live date, and three
 * mini-stats fed by `useProfiles`. The card has a subtle mesh-gradient
 * background and an avatar in a soft ring.
 *
 * Designed to be the first thing a user sees when they log in.
 */
import React, { useMemo } from "react";
import {
    FiUsers,
    FiSun,
    FiCloud,
    FiCloudSnow,
    FiCloudRain,
} from "react-icons/fi";
import { useAuth } from "../../auth/AuthContext";
import { useProfiles } from "../../hooks/useProfiles";
import { AnimatedCounter, Avatar, Skeleton } from "../../theme/components";
import { formatToday } from "../../utils/dates";
import "./WelcomeHero.css";

function formatDate(d) {
    return formatToday();
}

function pickGreetingIcon(date) {
    const h = date.getHours();
    if (h < 6) return FiCloudSnow;
    if (h < 12) return FiSun;
    if (h < 18) return FiCloud;
    return FiCloudRain;
}

export function WelcomeHero() {
    const { user } = useAuth();
    const { data: profiles, loading: profilesLoading, error: profilesError } = useProfiles();

    const greeting = useMemo(() => {
        const firstName = user?.fullName?.split(" ")[0];
        const hour = new Date().getHours();
        const partOfDay =
            hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
        return firstName ? `${partOfDay}, ${firstName}` : partOfDay;
    }, [user]);

    const role = user?.role || "Pediatrician";

    const today = useMemo(() => new Date(), []);
    const formattedDate = useMemo(() => formatDate(today), [today]);
    const GreetingIcon = useMemo(() => pickGreetingIcon(today), [today]);

    const totalChildren = Array.isArray(profiles) ? profiles.length : 0;

    const loading = profilesLoading;
    const hasError = Boolean(profilesError);

    return (
        <section className="wp-hero" aria-label="Welcome" aria-busy={loading || undefined}>
            <div className="wp-hero__bg" aria-hidden="true" />

            {loading ? (
                <WelcomeHeroSkeleton />
            ) : (
                <>
                    <div className="wp-hero__top">
                        <div className="wp-hero__identity">
                            <div className="wp-hero__avatar-ring">
                                <Avatar name={user?.fullName} id={user?.id} size={56} />
                            </div>
                            <div className="wp-hero__text">
                                <span className="wp-hero__eyebrow">
                                    <GreetingIcon size={14} strokeWidth={2.5} />
                                    {formattedDate}
                                </span>
                                <h1 className="wp-hero__title">{greeting}</h1>
                            </div>
                        </div>

                        <div className="wp-hero__role" aria-label="Your role">
                            <span className="wp-hero__role-dot" aria-hidden="true" />
                            {role}
                        </div>
                    </div>

                    <div className="wp-hero__stats">
                        <div className="wp-hero__stat">
                            <span className="wp-hero__stat-icon" style={{ color: "var(--pt-primary)" }}>
                                <FiUsers size={18} strokeWidth={2.5} />
                            </span>
                            <div className="wp-hero__stat-body">
                                <span className="wp-hero__stat-label">Total children</span>
                                <span className="wp-hero__stat-value">
                                    {hasError ? "—" : <AnimatedCounter value={totalChildren} />}
                                </span>
                            </div>
                        </div>

                    </div>
                </>
            )}
        </section>
    );
}

// Mirrors the real hero's three sections: identity (avatar + greeting +
// subtitle + role pill) and the 3-stat strip. Same `.wp-hero` shell, so
// dimensions match the real layout — no jump on swap-in.
function WelcomeHeroSkeleton() {
    return (
        <>
            <div className="wp-hero__top">
                <div className="wp-hero__identity">
                    <div className="wp-hero__avatar-ring wp-hero__avatar-ring--skeleton">
                        <Skeleton variant="circular" width={56} height={56} />
                    </div>
                    <div className="wp-hero__text wp-hero__text--skeleton">
                        <Skeleton variant="text" width={170} height={12} />
                        <Skeleton variant="text" width={260} height={32} />
                        <Skeleton variant="text" width={220} height={14} />
                    </div>
                </div>
                <Skeleton variant="rounded" width={110} height={28} className="wp-hero__role-skeleton" />
            </div>

            <div className="wp-hero__stats">
                {[0, 1].map((i) => (
                    <div key={i} className="wp-hero__stat">
                        <Skeleton variant="rounded" width={36} height={36} />
                        <div className="wp-hero__stat-body">
                            <Skeleton variant="text" width="65%" height={11} />
                            <Skeleton variant="text" width="45%" height={22} />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

export default WelcomeHero;
